<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { UiAsyncCombobox, type ComboboxItemData } from '@shopexperts/shared';
import type { City } from '@shopexperts/legacy-api';
import { cityLabel, cityOption, useCountryCitySearch } from '@/app/composables/location/useCountryCities';

const props = withDefaults(defineProps<{
    country?: string | null;
    /** Stored city, so the selection has a label before any list has loaded. */
    initialCity?: City | null;
    id?: string;
    name?: string;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    error?: string;
    size?: 'md' | 'lg';
}>(), {
    country: null,
    initialCity: null,
    placeholder: 'Search cities',
    size: 'md',
});

/** GeoNames id as a string, '' when no city is chosen. Strings keep it interchangeable with UiFormBuilder text fields. */
const model = defineModel<string>({ default: '' });

const emit = defineEmits<{ (e: 'validate'): void }>();

const SEARCH_DEBOUNCE_MS = 250;

const inputText = ref('');
const debouncedText = ref('');
let debounceTimer: ReturnType<typeof setTimeout> | undefined;
watch(inputText, (value) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => { debouncedText.value = value ?? ''; }, SEARCH_DEBOUNCE_MS);
});

const selectedItems = ref<ComboboxItemData[]>([]);
const selectedLabel = computed(() => selectedItems.value[0]?.label ?? '');

/** The input echoes the chosen label; only settled text that differs from it is a search. */
const searchQuery = computed(() => {
    const text = debouncedText.value;
    return text && text !== selectedLabel.value && inputText.value !== selectedLabel.value ? text : '';
});

const { cities, isFetching, hasMore, fetchNextPage } = useCountryCitySearch(() => props.country, searchQuery);

const data = computed<ComboboxItemData[]>(() => cities.value.map(cityOption));

const selectedId = computed<string | undefined>({
    get: () => model.value || undefined,
    set: (value) => { model.value = typeof value === 'string' ? value : ''; },
});

function resolveSelected(value: string) {
    if (!value || selectedItems.value[0]?.value === value) return;
    const known = cities.value.find((city) => String(city.id) === value);
    const stored = props.initialCity && String(props.initialCity.id) === value ? props.initialCity : null;
    const city = known ?? stored;
    if (!city) return;
    selectedItems.value = [cityOption(city)];
    inputText.value = cityLabel(city);
}

watch(() => model.value, (value) => {
    if (value) {
        resolveSelected(value);
        return;
    }
    selectedItems.value = [];
    inputText.value = '';
}, { immediate: true });

/* Search results and the stored city arrive after the model; they only ever add the label, never clear typed text. */
watch([() => props.initialCity, cities], () => resolveSelected(model.value));

const effectivePlaceholder = computed(() => (props.country ? props.placeholder : 'Select a country first'));
</script>

<template>
    <UiAsyncCombobox
        v-model="inputText"
        v-model:selected-id="selectedId"
        v-model:selected-items="selectedItems"
        :id="props.id"
        :name="props.name"
        :data="data"
        :is-fetching="isFetching"
        :has-more="hasMore"
        :placeholder="effectivePlaceholder"
        :disabled="props.disabled || !props.country"
        :invalid="props.invalid"
        :error="props.error"
        :size="props.size"
        display-mode="combobox"
        mode="preserve"
        @load-more="fetchNextPage"
        @blur="emit('validate')"
    />
</template>
